import { useState } from "react";
import { Check, ChevronDown, ChevronRight } from "lucide-react";
import { Button, Popover, PopoverContent, PopoverTrigger } from "@repo/ui";
import { cn } from "@repo/utils";
import type { Id } from "../../../convex/_generated/dataModel";
import { libelleVersion, type ScorecardOption } from "./scorecardsCampagne";

/**
 * Choix de la version de scorecard d'un entretien structuré : un menu où
 * chaque scorecard se déplie (une seule à la fois) sur ses versions publiées.
 * Déplier ne choisit rien : c'est le clic sur une version qui la retient.
 */
export function SelecteurVersionScorecard({
  scorecards,
  versionId,
  onChange,
  id,
}: {
  /** `undefined` pendant le chargement. */
  scorecards: ScorecardOption[] | undefined;
  versionId: Id<"scorecardVersions"> | undefined;
  onChange: (slug: string, versionId: Id<"scorecardVersions">) => void;
  id?: string;
}) {
  const [ouvert, setOuvert] = useState(false);
  const [deplie, setDeplie] = useState<string | null>(null);

  const choisie = scorecards?.find((s) => s.versions.some((v) => v._id === versionId));
  const version = choisie?.versions.find((v) => v._id === versionId);

  const ouvrir = (etat: boolean) => {
    // À l'ouverture, la scorecard de la version retenue est dépliée.
    if (etat) setDeplie(choisie?.slug ?? null);
    setOuvert(etat);
  };

  const libelle =
    scorecards === undefined
      ? "Chargement…"
      : choisie && version
        ? `${choisie.titre} — v${version.numero}`
        : scorecards.length === 0
          ? "Aucune scorecard publiée"
          : "Sélectionner une version";

  return (
    <Popover open={ouvert} onOpenChange={ouvrir}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={!scorecards?.length}
          className={cn("w-full justify-between font-normal", !version && "text-muted-foreground")}
        >
          <span className="truncate">{libelle}</span>
          <ChevronDown className="h-4 w-4 opacity-60" />
        </Button>
      </PopoverTrigger>
      {/* `p-1` : la marge garde l'anneau de focus des lignes pleine largeur
          à l'intérieur de la zone défilante. */}
      <PopoverContent align="start" className="w-(--radix-popover-trigger-width) min-w-72 p-1">
        <ul className="max-h-80 overflow-y-auto p-1">
          {(scorecards ?? []).map((s) => {
            const estDeplie = deplie === s.slug;
            const idVersions = `${id ?? "scorecard"}-versions-${s.slug}`;
            return (
              <li key={s._id}>
                <button
                  type="button"
                  aria-expanded={estDeplie}
                  aria-controls={idVersions}
                  onClick={() => setDeplie(estDeplie ? null : s.slug)}
                  className="flex w-full items-center gap-2 rounded-controle px-2 py-1.5 text-left text-sm font-medium hover:bg-charte-hover"
                >
                  {estDeplie ? (
                    <ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0 opacity-60" />
                  )}
                  <span className="truncate">{s.titre}</span>
                </button>
                {estDeplie && (
                  <div id={idVersions} role="radiogroup" aria-label={`Versions de ${s.titre}`}>
                    {s.versions.map((v) => (
                      <button
                        key={v._id}
                        type="button"
                        role="radio"
                        aria-checked={v._id === versionId}
                        onClick={() => {
                          onChange(s.slug, v._id);
                          setOuvert(false);
                        }}
                        className="flex w-full items-center gap-2 rounded-controle py-1.5 pr-2 pl-8 text-left text-sm hover:bg-charte-hover"
                      >
                        <span className="flex-1">{libelleVersion(v)}</span>
                        {v._id === versionId && <Check className="h-4 w-4" />}
                      </button>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
